// 裝置除錯記錄 面板(V4P-8,自 ls-v4 admin 三層 log 系統移植)。
// 啟用 → SSE 串流即時 devlog → 停用。SSE 用 fetch+ReadableStream(EventSource
// 不能帶 Authorization);後端鏈=restful 代理 → signaling :9091 devlog plane。
import { useCallback, useEffect, useRef, useState } from 'react';
import { config } from '../../config';
import { tokenStore } from '../../lib/api';

interface DevlogLine {
  ts_us: number;
  text: string;
}

const MAX_LINES = 500;

function authHeaders(): Record<string, string> {
  const h: Record<string, string> = { 'X-Client-Id': config.clientId, 'X-Client-Key': config.clientKey };
  const t = tokenStore.get();
  if (t) h['Authorization'] = 'Bearer ' + t;
  return h;
}

export function DeviceDevlogPanel({ deviceId }: { deviceId: string }) {
  const [on, setOn] = useState(false);
  const [lines, setLines] = useState<DevlogLine[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const base = `${config.apiBase}/v3/admin/devices/${encodeURIComponent(deviceId)}/devlog`;

  const stream = useCallback(async () => {
    const ac = new AbortController();
    abortRef.current = ac;
    try {
      const res = await fetch(`${base}/stream`, { headers: authHeaders(), signal: ac.signal });
      if (!res.ok || !res.body) {
        setErr(`串流連線失敗,請稍後再試（HTTP ${res.status}）`);
        return;
      }
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = '';
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const events = buf.split('\n\n');
        buf = events.pop() ?? '';
        for (const ev of events) {
          const m = ev.match(/^data: (.+)$/m);
          if (!m) continue;
          try {
            const line = JSON.parse(m[1]) as DevlogLine;
            setLines((cur) => [...cur.slice(-(MAX_LINES - 1)), line]);
          } catch {
            /* heartbeat / non-JSON */
          }
        }
      }
    } catch (e) {
      if (!ac.signal.aborted) setErr(e instanceof Error ? e.message : '串流中斷');
    }
  }, [base]);

  const enable = useCallback(async () => {
    setErr(null);
    setLines([]);
    const res = await fetch(base, { method: 'POST', headers: { ...authHeaders(), 'Content-Type': 'application/json' }, body: JSON.stringify({ duration_s: 900 }) });
    if (!res.ok) {
      setErr(`無法啟用追蹤,請確認你有權限且裝置在線（HTTP ${res.status}）`);
      return;
    }
    setOn(true);
    void stream();
  }, [base, stream]);

  const disable = useCallback(async () => {
    abortRef.current?.abort();
    abortRef.current = null;
    setOn(false);
    await fetch(base, { method: 'DELETE', headers: authHeaders() }).catch(() => {});
  }, [base]);

  useEffect(() => {
    return () => {
      abortRef.current?.abort();
      void fetch(base, { method: 'DELETE', headers: authHeaders() }).catch(() => {});
    };
  }, [base]);

  return (
    <section className="card">
      <h2 className="adm-card-title">裝置除錯記錄</h2>
      <p className="text-secondary" style={{ marginTop: 0 }}>
        即時追蹤此裝置在 signaling 的 wire/relay 追蹤(§14.4;15 分鐘自動關閉)。啟用會增加該裝置的 log 量,排查後請停用。
      </p>
      <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
        {!on ? (
          <button className="btn-primary btn-sm" onClick={() => void enable()} data-testid="devlog-enable">啟用追蹤</button>
        ) : (
          <button className="btn-ghost btn-sm" onClick={() => void disable()} data-testid="devlog-disable">停止追蹤</button>
        )}
        {on && <span className="adm-mono" style={{ fontSize: 'var(--font-size-xs)', opacity: 0.7, alignSelf: 'center' }} data-testid="devlog-live">● 串流中 · {lines.length} 行</span>}
      </div>
      {err && <p className="error-text" role="alert">{err}</p>}
      {on && (
        <pre
          data-testid="devlog-output"
          className="adm-devlog-output"
          style={{ maxHeight: 320, overflow: 'auto', margin: 0 }}
        >
          {lines.length === 0 ? '等待 devlog…(對此裝置產生流量即會出現,例如觸發事件或 live)' : lines.map((l) => `${new Date(l.ts_us / 1000).toLocaleTimeString('zh-Hant')}  ${l.text}`).join('\n')}
        </pre>
      )}
    </section>
  );
}
